<template>
  <div>
    <TableFilter
      v-if="modalIsActive && !preview"
      :preload="filter"
      @close="toggleFilter"
      :range="true"
      @save="setFilter"
    />
    <div class="columns is-vcentered is-multiline">
      <div class="column">
        <div class="columns is-mobile is-multiline is-vcentered">
          <div class="column is-narrow" v-if="rows">
            <p class="table-options-lookup">Lookup history</p>
          </div>
          <div class="column is-narrow" v-if="rows">
            <p class="table-options-showing" v-if="title">
              {{ title }}
            </p>
          </div>
          <div class="column is-narrow" v-if="preview">
            <router-link
              @click.native="track"
              :disabled="!rows"
              :to="{ name: 'account-activity-user' }"
              class="table-options-viewall"
            >
              View all
            </router-link>
          </div>
        </div>
      </div>
      <div v-if="!preview" class="column is-narrow">
        <div class="field is-grouped">
          <div class="control">
            <TableSearch
              @change="setSearch"
              placeholder="Start typing to search"
            />
          </div>
          <div class="control">
            <date-picker-button
              @toggleFilter="toggleFilter"
              @clearFilter="clearFilter"
              :to="query.to"
              :from="query.from"
            />
          </div>
          <div class="control" v-if="depth === 'branch' || depth === 'org'">
            <DownloadCSVDropdown
              :options="csvDownloadOptions"
              :disabled="csvDownloadDisabled"
              :tooltip="csvDownloadTooltip"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="columns is-vcentered is-multiline is-mobile">
      <div class="column is-12" v-if="depth === 'branch'">
        <SummaryBoxes
          v-if="usage"
          v-bind="{
            usage,
            isCompanionUser,
            hasAutoCheck
          }"
        />
      </div>
      <div class="column is-12">
        <Tabs
          v-bind="{
            hasAutoCheck,
            tab,
            preview,
            colour
          }"
          @change-tab="changeTab"
          data-test-id="product-tabs"
        />
      </div>
    </div>
    <p
      class="has-text-hard is-size-7"
      v-if="!isEmpty && tab === 'account-history-vpp'"
    >
      <span class="icon is-small">
        <i class="fal fa-info-circle" />
      </span>
      <span>Clicking on a previous search will count as a separate lookup</span>
    </p>
    <table
      class="table is-fullwidth is-size-7-mobile is-striped is-transparent"
      v-if="!isEmpty"
    >
      <TableHeader
        v-bind="{ columns, sort, preview, depth }"
        @change="setSort"
      />
      <component :is="tab" v-bind="{ preview, depth, rows, columns }" />
    </table>
    <TableEmpty
      v-else
      text="Nothing to see here"
      subtext="We can't find any searches for you"
    />
    <Pagination
      v-if="!isEmpty && !preview"
      :fn="'fetch'"
      v-bind="{ page, pages }"
      @change="setPage"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import tableSearch from 'core/modules/table-search/mixins/tableSearch.mixin'
import tableFilter from 'core/modules/table-filter/mixins/tableFilter.mixin'
import tableHeader from 'core/modules/table-header/mixins/tableHeader.mixin'
import pagination from 'core/modules/pagination/mixins/pagination.mixin'
import activityTable from 'core/mixins/activityTable'
import * as account from 'modules/account/services'
import { buildDescription } from 'modules/autocheck/services'
import AccountHistoryMotorcheck from './AccountHistoryMotorcheck'

export default {
  name: 'AccountHistoryTable',
  components: {
    AccountHistoryCheck: () => import('./AccountHistoryCheck'),
    AccountHistoryVpp: () => import('./AccountHistoryVpp'),
    AccountHistoryCompanion: () => import('./AccountHistoryCompanion'),
    AccountHistoryMotorcheck: () => import('./AccountHistoryMotorcheck'),
    TableSearch: () =>
      import('core/modules/table-search/components/TableSearch'),
    TableFilter: () =>
      import('core/modules/table-filter/components/TableFilter'),
    TableHeader: () =>
      import('core/modules/table-header/components/TableHeader'),
    TableEmpty: () => import('core/components/TableEmpty'),
    DatePickerButton: () =>
      import('core/modules/table-filter/components/DatePickerButton'),
    DownloadCSVDropdown: () =>
      import('@/modules/csv/components/DownloadCSVDropdown'),
    Tabs: () => import('./AccountHistoryProductTabs'),
    SummaryBoxes: () => import('./AccountHistorySummaryBoxes')
  },
  props: {
    depth: {
      type: String,
      required: false,
      default: () => 'user'
    },
    preview: {
      type: Boolean,
      required: false,
      default: () => false
    }
  },
  data: () => ({
    tab: '',
    response: null,
    usage: null
  }),
  mixins: [
    activityTable,
    pagination({
      fn: {
        data: 'response',
        fetch: 'fetch'
      }
    }),
    tableSearch({ fetch: 'fetch' }),
    tableFilter({ fetch: 'fetch' }),
    tableHeader({ fetch: 'fetch' })
  ],
  created() {
    const preselect = this.$route.query.tab

    const tabs = {
      autocheck: 'account-history-check',
      companion: 'account-history-companion',
      vpp: 'account-history-vpp',
      motorcheck: 'account-history-motorcheck'
    }

    if (preselect) {
      this.tab = tabs[preselect]
    } else {
      this.tab = this.$experian
        ? tabs.autocheck
        : this.isCompanionUser
        ? tabs.companion
        : tabs.vpp
    }

    this.fetch()
  },
  computed: {
    ...mapGetters('auth', [
      'isCompanionUser',
      'hasAutoCheck',
      'hasMotorCheck',
      'branch',
      'organisation',
      'hasCustomerReference'
    ]),
    csvDownloadDisabled() {
      const { from, to } = this.query
      if (!from || !to) return true
      const f = new Date(from)
      const t = new Date(to)
      const limit = new Date(f)
      limit.setMonth(limit.getMonth() + 2)
      return t > limit
    },
    csvDownloadTooltip() {
      const { from, to } = this.query
      if (!from || !to) return 'Select a date range to download'
      if (this.csvDownloadDisabled) return 'Date range must be less than 2 months'
      return this.depth === 'org' ? 'Download organisation activity' : 'Download branch activity'
    },
    csvDateRange() {
      const { from, to } = this.query
      if (!from || !to) return ''
      const f = new Date(from)
      const t = new Date(to)
      const fmt = d => `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`
      return `${fmt(f)}-to-${fmt(t)}`
    },
    csvEntityName() {
      if (this.depth === 'org') return this.organisation?.name || 'organisation'
      if (this.depth === 'branch') return this.branch?.name || 'branch'
      return 'user'
    },
    csvDownloadOptions() {
      const { allCsvColumns, csvDateRange, csvEntityName } = this
      const options = []
      const namePrefix = csvEntityName.toLowerCase().replace(/\s+/g, '-')

      if (this.depth === 'branch' || this.depth === 'org') {
        options.push({
          label: 'All activity',
          columns: allCsvColumns,
          getEntries: this.fetchAllCsvEntries,
          filename: `${namePrefix}-all-activity-${csvDateRange}`
        })
      }

      if (this.isCompanionUser) {
        options.push({
          label: 'Companion activity',
          columns: this.getCsvColumnsFor('account-history-companion'),
          getEntries: () => this.fetchCsvEntriesFor('getCompanion'),
          filename: `${namePrefix}-companion-${csvDateRange}`
        })
      }

      if (this.hasAutoCheck) {
        options.push({
          label: 'AutoCheck activity',
          columns: this.getCsvColumnsFor('account-history-check'),
          getEntries: () => this.fetchAutoCheckCsvEntries(),
          filename: `${namePrefix}-autocheck-${csvDateRange}`
        })
      }

      options.push({
        label: 'VPP activity',
        columns: this.getCsvColumnsFor('account-history-vpp'),
        getEntries: () => this.fetchCsvEntriesFor('getVpp'),
        filename: `${namePrefix}-vpp-${csvDateRange}`
      })

      return options
    },
    icon() {
      if (this.depth === 'org') return 'fa-university'
      if (this.depth === 'branch') return 'fa-building'
      else return 'fa-search'
    },
    colour() {
      return this.tab === 'account-history-companion' ? 'orange' : 'celadon'
    },
    total() {
      return this.response?.total
    },
    title() {
      return `Showing ${this.rows?.length} of ${this.total}`
    },
    service() {
      const map = {
        'account-history-companion': 'getCompanion',
        'account-history-check': 'getChecks',
        'account-history-vpp': 'getVpp',
        'account-history-motorcheck': 'getMotorCheck',
        'account-history-auto-check-valuation': 'getAutoCheckValuation'
      }
      return map[this.tab]
    },
    isEmpty() {
      return this.response?.results?.length === 0
    },
    check() {
      return [
        { label: 'VRM', type: 'vrm', query: true },
        { label: 'Vehicle description', hideOnMobile: true },
        { label: 'Date', type: 'createdAt', query: true },
        { label: 'Check status' },
        { label: 'Actions' }
      ]
    },
    companion() {
      return [
        { label: 'VRM', type: 'vrm', query: true },
        { label: 'Reference', hideOnMobile: true },
        { label: 'Vehicle description', hideOnMobile: true },
        { label: 'Trade value', hideOnMobile: true },
        { label: 'Retail value', hideOnMobile: true },
        { label: 'Mileage', hideOnMobile: true },
        { label: 'Date', type: 'createdAt', query: true },
        { label: 'Actions' }
      ]
    },
    autoCheckValuation() {
      return [
        { label: 'VRM', type: 'vrm', query: true },
        { label: 'Vehicle description', hideOnMobile: true },
        { label: 'Mileage', hideOnMobile: true },
        { label: 'Date', type: 'createdAt', query: true },
        { label: 'Cazana' },
        { label: 'Glass' },
        { label: 'CAP' },
        { label: 'Actions' }
      ]
    },
    vpp() {
      return [
        { label: 'VRM', type: 'vrm', query: true },
        { label: 'Vehicle description', hideOnMobile: true },
        { label: 'Mileage', hideOnMobile: true },
        { label: 'Date', type: 'createdAt', query: true },
        { label: 'Actions' }
      ]
    },
    motorcheck() {
      return [
        { label: 'VRM', type: 'vrm', query: true },
        { label: 'Vehicle description', hideOnMobile: true },
        { label: 'Date', type: 'createdAt', query: true },
        { label: 'Status' },
        { label: 'Report' }
      ]
    },
    columns() {
      const {
        tab,
        depth,
        check,
        companion,
        autoCheckValuation,
        vpp,
        motorcheck
      } = this
      const user = { label: 'User', hideOnMobile: true }
      const branch = { label: 'Branch', hideOnMobile: true }

      if (tab === 'account-history-check') {
        if (depth === 'org') {
          return [...check.slice(0, 3), user, branch, ...check.slice(3)]
        } else if (depth === 'branch') {
          return [...check.slice(0, 3), user, ...check.slice(3)]
        } else {
          return check
        }
      } else if (tab === 'account-history-companion') {
        if (depth === 'org') {
          return [...companion.slice(0, 7), user, branch, companion[7]]
        } else if (depth === 'branch') {
          return [...companion.slice(0, 7), user, companion[7]]
        } else {
          return companion
        }
      } else if (tab === 'account-history-auto-check-valuation') {
        if (depth === 'org') {
          return [
            ...autoCheckValuation.slice(0, 7),
            user,
            branch,
            ...autoCheckValuation.slice(7)
          ]
        } else if (depth === 'branch') {
          return [
            ...autoCheckValuation.slice(0, 7),
            user,
            ...autoCheckValuation.slice(7)
          ]
        } else {
          return autoCheckValuation
        }
      } else if (tab === 'account-history-motorcheck') {
        if (depth === 'org') {
          return [
            ...motorcheck.slice(0, 3),
            user,
            branch,
            ...motorcheck.slice(3)
          ]
        } else if (depth === 'branch') {
          return [...motorcheck.slice(0, 3), ...motorcheck.slice(3)]
        } else {
          return motorcheck
        }
      } else {
        if (depth === 'org') {
          return [...vpp.slice(0, 4), user, branch, ...vpp.slice(4)]
        } else if (depth === 'branch') {
          return [...vpp.slice(0, 4), user, ...vpp.slice(4)]
        } else {
          return vpp
        }
      }
    },
    allCsvColumns() {
      const cols = [
        { label: 'Type', path: 'type' },
        { label: 'VRM', path: 'vrm' },
        { label: 'Date', path: 'date', format: 'date' }
      ]
      if (this.depth !== 'user') cols.push({ label: 'User', path: 'user' })
      if (this.depth !== 'user') cols.push({ label: 'Email', path: 'email' })
      if (this.depth === 'org') cols.push({ label: 'Branch', path: 'branch' })
      if (this.isCompanionUser) {
        cols.push(
          { label: 'VIN', path: 'vin' },
        )
        if (this.hasCustomerReference) cols.push({ label: 'Customer reference', path: 'customerReference' })
        cols.push(
          { label: 'Description', path: 'description' },
          { label: 'Trade value', path: 'tradeValue' },
          { label: 'Retail value', path: 'retailValue' },
          { label: 'Mileage', path: 'mileage' },
          { label: 'Valuation date', path: 'valuationDate', format: 'date' }
        )
      }
      return cols
    },
    csvParams() {
      const { depth, query } = this
      return { ...query, limit: 1000000, page: 1, depth }
    },
    limit() {
      return this.preview ? 5 : 12
    },
    rows() {
      return this.response?.results
    }
  },
  methods: {
    track() {
      this.$mxp.track('dashboard_user_history_view_all')
    },
    async fetch(req) {
      const { limit, query, depth } = this

      try {
        this.$wait.start('response')
        if (depth === 'branch') this.getBranchUsage()

        const params = { limit, depth, ...query, ...req }
        this.response = await account[this.service](params)
      } catch (err) {
        if (err?.response?.status !== 401) {
          this.$notify('History unavailable')
        }
      } finally {
        this.$wait.end('response')
      }
    },
    toggleFilter() {
      this.$mxp.track(`${this.depth}_history_companion_open_date_filter`)
      this.modalIsActive = !this.modalIsActive
    },
    changeTab(tab) {
      if (tab === 'account-history-companion' && !this.isCompanionUser) {
        return this.$modal.open('companion/UpsellCompanion')
      }
      this.tab = tab
      this.page = 1
      this.fetch()
    },
    getCsvColumnsFor(tab) {
      const { depth } = this
      const cols = []
      const userCol = { label: 'User', path: 'user.name' }
      const branchCol = { label: 'Branch', path: 'branch.name' }

      if (tab === 'account-history-companion') {
        cols.push(
          { label: 'VRM', path: 'log.vrm' },
          { label: 'VIN', path: 'log.vin' },
        )
        if (this.hasCustomerReference) cols.push({ label: 'Customer reference', path: 'request.customerReference' })
        cols.push(
          { label: 'Description', path: 'data.description' },
          { label: 'Trade value', path: 'data.remarket' },
          { label: 'Retail value', path: 'data.retail' },
          { label: 'Mileage', path: 'data.mileage.value' },
          { label: 'Report date', path: 'createdAt', format: 'date' },
          { label: 'Valuation date', path: 'data.date', format: 'date' }
        )
      } else if (tab === 'account-history-check') {
        cols.push(
          { label: 'VRM', path: 'vrm' },
          { label: 'VIN', path: 'vin' },
          { label: 'Description', path: 'description' },
          { label: 'Date', path: 'createdAt', format: 'date' }
        )
      } else if (tab === 'account-history-vpp') {
        cols.push(
          { label: 'VRM', path: 'vrm' },
          { label: 'Description', path: 'description' },
          { label: 'Mileage', path: 'mileage' },
          { label: 'Date', path: 'createdAt', format: 'date' }
        )
      } else if (tab === 'account-history-motorcheck') {
        cols.push(
          { label: 'VRM', path: 'vrm' },
          { label: 'Date', path: 'createdAt', format: 'date' }
        )
      }

      if (depth !== 'user') cols.push(userCol)
      if (depth !== 'user' && tab === 'account-history-companion') {
        cols.push({ label: 'Email', path: 'user.email' })
      }
      if (depth === 'org') cols.push(branchCol)

      return cols
    },
    async fetchCsvEntriesFor(service) {
      const params = { ...this.csvParams }
      const response = await account[service](params)
      return response.results
    },
    async fetchAutoCheckCsvEntries() {
      const params = { ...this.csvParams }
      const response = await account.getChecks(params)
      return response.results.map(row => {
        row.description = row.response ? buildDescription(row.response.response) : ''
        return row
      })
    },
    async fetchAllCsvEntries() {
      const { depth } = this
      const params = { ...this.csvParams }
      const requests = []

      const normalize = (type, row, vrm) => {
        const entry = { type, vrm, date: row.createdAt, user: row.user?.name || '', email: row.user?.email || '' }
        if (depth === 'org') entry.branch = row.branch?.name || ''
        return entry
      }

      if (this.isCompanionUser) requests.push(account.getCompanion(params).then(res => res.results.map(row => {
        const entry = normalize('Companion', row, row.log?.vrm || '')
        entry.vin = row.log?.vin || ''
        entry.customerReference = row.request?.customerReference || ''
        entry.description = row.data?.description || ''
        entry.tradeValue = row.data?.remarket || ''
        entry.retailValue = row.data?.retail || ''
        entry.mileage = row.data?.mileage?.value || ''
        entry.valuationDate = row.data?.date || ''
        return entry
      })))
      if (this.hasAutoCheck) requests.push(account.getChecks(params).then(res => res.results.map(row => {
        const entry = normalize('AutoCheck', row, row.vrm || '')
        entry.vin = row.vin || ''
        entry.description = row.response ? buildDescription(row.response.response) : ''
        return entry
      })))
      requests.push(account.getVpp(params).then(res => res.results.map(row => normalize('VPP', row, row.vrm || ''))))

      const results = await Promise.all(requests)
      return results.flat().sort((left, right) => new Date(right.date) - new Date(left.date))
    },
    async getBranchUsage() {
      this.$wait.start('usage')
      const { from, to } = this.query
      try {
        this.usage = await account.getBranchUsage({ from, to })
      } catch (err) {
        if (err?.response?.status !== 401) this.$notify('Usage unavailable')
      } finally {
        this.$wait.end('usage')
      }
    }
  }
}
</script>

<style lang="sass" scoped>
.tabs
  overflow-x: hidden
.table-options-lookup
  font-weight: $history-table-options-lookup-font-weight
  color: $history-table-options-lookup-text-color
.table-options-showing
  font-weight: $history-table-options-showing-font-weight
  color: $history-table-options-showing-text-color
.table-options-viewall
  text-decoration: $history-table-options-text-decoration
  font-weight: $history-table-options-viewall-font-weight
  color: $history-table-options-viewall-text-color
  &:hover
    color: $history-table-options-viewall-text-hover
</style>
